import { useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowUpRight,
  CheckCircle2,
  Loader2,
  Paperclip,
  X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  { v: "handyman", l: "Handyman / Small Repair" },
  { v: "residential", l: "Residential Renovation" },
  { v: "residential_new", l: "New Residential Build" },
  { v: "commercial", l: "Commercial / Large-Scale" },
  { v: "emergency", l: "24/7 Emergency" },
  { v: "other", l: "Other" },
];

const WINDOWS = [
  { v: "morning", l: "Morning", h: "7am – 11am" },
  { v: "midday", l: "Midday", h: "11am – 2pm" },
  { v: "afternoon", l: "Afternoon", h: "2pm – 5pm" },
  { v: "evening", l: "Evening", h: "5pm – 8pm" },
];

// Build the next 14 days (skip today) as pickable slots.
function useDays(count = 14) {
  return useMemo(() => {
    const out = [];
    const now = new Date();
    for (let i = 1; i <= count; i++) {
      const d = new Date(now);
      d.setDate(now.getDate() + i);
      const iso = d.toISOString().slice(0, 10);
      out.push({
        iso,
        dow: d.toLocaleDateString(undefined, { weekday: "short" }),
        day: d.getDate(),
        month: d.toLocaleDateString(undefined, { month: "short" }),
      });
    }
    return out;
  }, [count]);
}

export default function Estimate() {
  const days = useDays(14);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    project_type: "residential",
    scope: "",
    preferred_date: days[0]?.iso ?? "",
    preferred_window: "morning",
  });
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      const ups = [];
      for (const f of Array.from(files)) {
        if (f.size > 15 * 1024 * 1024) {
          toast.error(`${f.name} exceeds 15MB`);
          continue;
        }
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await axios.post(`${API}/uploads`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ups.push({ id: data.id, name: data.original_filename });
      }
      setAttachments((p) => [...p, ...ups]);
      if (ups.length) toast.success(`${ups.length} file(s) attached`);
    } catch (e) {
      console.error(e);
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const required = ["name", "email", "phone", "address", "scope"];
    for (const k of required) {
      if (!form[k]) {
        toast.error("Please fill all fields.");
        return;
      }
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/estimates`, {
        ...form,
        attachment_ids: attachments.map((a) => a.id),
      });
      setDone(true);
      toast.success("Estimate visit requested — we'll confirm within 2 hours.");
      setAttachments([]);
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail ??
        "Something went wrong. Please call 610-349-7313.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="estimate"
      data-testid="estimate-section"
      className="relative border-y border-white/10 bg-[#0a0a0a] py-28 md:py-40"
    >
      <div className="pointer-events-none absolute inset-0 -z-10 grain" />

      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <div className="grid gap-10 md:grid-cols-[1.05fr_1.4fr] md:items-start md:gap-20">
          <motion.header
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
          >
            <div className="eyebrow">— Free On-Site Estimate</div>
            <h2 className="mt-6 font-oswald text-5xl uppercase leading-[0.9] tracking-tight text-white md:text-7xl">
              Book a
              <br />
              <span className="text-[color:var(--accent)]">walk-through.</span>
            </h2>
            <p className="mt-8 max-w-md font-editorial text-lg italic text-white/70 md:text-xl">
              Pick a day, pick a window. A JLP estimator drives out, measures,
              talks it through, and emails you a written quote &mdash; usually
              within 48 hours. Free. No pressure. No obligation.
            </p>

            <ul className="mt-10 space-y-3 border-t border-white/10 pt-6">
              {[
                "Fully licensed, insured, bonded in PA.",
                "Fixed-price written estimate — not a range.",
                "Same crew from estimate to keys.",
              ].map((t) => (
                <li
                  key={t}
                  className="flex items-start gap-3 text-sm text-white/70"
                >
                  <CheckCircle2
                    size={16}
                    className="mt-[3px] shrink-0 text-[color:var(--accent)]"
                  />
                  {t}
                </li>
              ))}
            </ul>
          </motion.header>

          <motion.form
            data-testid="estimate-form"
            onSubmit={submit}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.2, 0.8, 0.2, 1] }}
            className="relative border border-white/10 bg-black p-6 md:p-10"
          >
            {done && (
              <div
                data-testid="estimate-success"
                className="mb-8 flex items-center gap-3 border border-[color:var(--accent)]/40 bg-[color:var(--accent)]/5 px-4 py-3 text-sm text-white"
              >
                <CheckCircle2
                  size={18}
                  className="text-[color:var(--accent)]"
                />
                Visit requested. You&apos;ll get a text confirmation within 2
                hours.
              </div>
            )}

            <StepLabel n="01" label="Who & where" />
            <div className="mt-4 grid gap-5 md:grid-cols-2">
              <Field
                label="Full name"
                testid="estimate-name"
                value={form.name}
                onChange={(v) => setForm((f) => ({ ...f, name: v }))}
              />
              <Field
                label="Email"
                type="email"
                testid="estimate-email"
                value={form.email}
                onChange={(v) => setForm((f) => ({ ...f, email: v }))}
              />
              <Field
                label="Phone"
                type="tel"
                testid="estimate-phone"
                value={form.phone}
                onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
              />
              <Field
                label="Property address"
                testid="estimate-address"
                value={form.address}
                onChange={(v) => setForm((f) => ({ ...f, address: v }))}
              />
            </div>

            <StepLabel n="02" label="Project" className="mt-10" />
            <div className="mt-4 grid gap-5 md:grid-cols-[1fr_1.4fr]">
              <div className="flex flex-col gap-2">
                <SmallLabel>Type</SmallLabel>
                <select
                  data-testid="estimate-project-type"
                  value={form.project_type}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, project_type: e.target.value }))
                  }
                  className="border border-white/15 bg-transparent px-3 py-3 font-oswald text-sm uppercase tracking-widest text-white outline-none focus:border-[color:var(--accent)]"
                >
                  {PROJECT_TYPES.map((p) => (
                    <option
                      key={p.v}
                      value={p.v}
                      className="bg-[#0a0a0a] text-white"
                    >
                      {p.l}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <SmallLabel>Scope / what you want done</SmallLabel>
                <textarea
                  data-testid="estimate-scope"
                  rows={3}
                  value={form.scope}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, scope: e.target.value }))
                  }
                  placeholder="e.g. Open the wall between kitchen and dining, new island, quartz counters."
                  className="resize-none border border-white/15 bg-transparent px-3 py-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-[color:var(--accent)]"
                />
              </div>
            </div>

            <StepLabel n="03" label="Preferred visit" className="mt-10" />

            <SmallLabel className="mt-4">Day</SmallLabel>
            <div
              className="mt-3 grid grid-cols-4 gap-2 md:grid-cols-7"
              data-testid="estimate-day-picker"
            >
              {days.map((d) => {
                const active = form.preferred_date === d.iso;
                return (
                  <button
                    key={d.iso}
                    type="button"
                    data-testid={`estimate-day-${d.iso}`}
                    onClick={() =>
                      setForm((f) => ({ ...f, preferred_date: d.iso }))
                    }
                    className={`group flex flex-col items-start border p-3 text-left transition ${
                      active
                        ? "border-[color:var(--accent)] bg-[color:var(--accent)]/10 text-white"
                        : "border-white/10 text-white/70 hover:border-white/30 hover:text-white"
                    }`}
                  >
                    <span className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                      {d.dow}
                    </span>
                    <span className="mt-1 font-oswald text-2xl leading-none">
                      {d.day}
                    </span>
                    <span className="mt-1 font-mono text-[10px] uppercase tracking-widest text-white/40">
                      {d.month}
                    </span>
                  </button>
                );
              })}
            </div>

            <SmallLabel className="mt-6">Time window</SmallLabel>
            <div
              className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-4"
              data-testid="estimate-window-picker"
            >
              {WINDOWS.map((w) => {
                const active = form.preferred_window === w.v;
                return (
                  <button
                    key={w.v}
                    type="button"
                    data-testid={`estimate-window-${w.v}`}
                    onClick={() =>
                      setForm((f) => ({ ...f, preferred_window: w.v }))
                    }
                    className={`flex flex-col items-start border p-4 text-left transition ${
                      active
                        ? "border-[color:var(--accent)] bg-[color:var(--accent)]/10 text-white"
                        : "border-white/10 text-white/70 hover:border-white/30 hover:text-white"
                    }`}
                  >
                    <span className="font-oswald text-lg uppercase tracking-tight">
                      {w.l}
                    </span>
                    <span className="mt-1 font-mono text-[10px] uppercase tracking-widest text-white/40">
                      {w.h}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Attachments (optional) */}
            <div className="mt-8">
              <SmallLabel>
                Optional — attach photos or plans (max 15MB each)
              </SmallLabel>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  data-testid="estimate-attach-btn"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-oswald text-xs uppercase tracking-widest text-white transition hover:border-white/50 disabled:opacity-60"
                >
                  {uploading ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Paperclip size={14} />
                  )}
                  {uploading ? "Uploading…" : "Attach photos"}
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  accept="image/*,application/pdf"
                  onChange={(e) => handleFiles(e.target.files)}
                  className="hidden"
                  data-testid="estimate-file-input"
                />
                {attachments.map((a) => (
                  <span
                    key={a.id}
                    data-testid={`estimate-attachment-${a.id}`}
                    className="inline-flex items-center gap-2 border border-white/15 bg-white/5 px-3 py-2 text-xs text-white/85"
                  >
                    <Paperclip size={12} />
                    <span className="max-w-[160px] truncate">{a.name}</span>
                    <button
                      type="button"
                      onClick={() =>
                        setAttachments((p) => p.filter((x) => x.id !== a.id))
                      }
                      className="text-white/50 hover:text-white"
                      aria-label={`Remove ${a.name}`}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-10 flex flex-col-reverse items-start justify-between gap-6 border-t border-white/10 pt-8 md:flex-row md:items-center">
              <span className="text-xs leading-relaxed text-white/45">
                Prefer to skip this?{" "}
                <a
                  href="tel:6103497313"
                  className="border-b border-white/30 text-white/80 hover:text-white"
                >
                  Call 610-349-7313
                </a>{" "}
                — we answer 24/7. Or email{" "}
                <a
                  data-testid="estimate-email-link"
                  href="mailto:info@jlp-builds.com"
                  className="border-b border-white/30 text-white/80 hover:text-white"
                >
                  info@jlp-builds.com
                </a>
                .
              </span>

              <button
                type="submit"
                data-testid="estimate-submit"
                disabled={submitting}
                className="btn-primary disabled:opacity-70"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> Booking…
                  </>
                ) : (
                  <>
                    Request Free Estimate <ArrowUpRight size={16} />
                  </>
                )}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function StepLabel({ n, label, className = "" }) {
  return (
    <div
      className={`flex items-baseline gap-4 border-b border-white/10 pb-3 ${className}`}
    >
      <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/40">
        Step {n}
      </span>
      <span className="font-oswald text-xl uppercase tracking-tight text-white">
        {label}
      </span>
    </div>
  );
}

function SmallLabel({ children, className = "" }) {
  return (
    <label
      className={`font-mono text-[11px] uppercase tracking-[0.3em] text-white/50 ${className}`}
    >
      {children}
    </label>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div className="flex flex-col gap-2">
      <SmallLabel>{label}</SmallLabel>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-white/15 bg-transparent px-3 py-3 text-sm text-white placeholder:text-white/30 outline-none focus:border-[color:var(--accent)]"
      />
    </div>
  );
}
